<div class="page">
  <header class="page-head">
    <div>
      <div class="overline">Platform</div>
      <h1>Users Management</h1>
      <p class="lede">
        Search users and reset passwords securely from one place.
      </p>
    </div>
  </header>

  @if (!isPlatformAdmin) {
    <section class="card unauthorized">
      <h2>Access restricted</h2>
      <p>Only platform administrators can use this page.</p>
    </section>
  } @else {
    @if (temporaryPassword && lastResetUserEmail) {
      <section class="card temporary-password-panel" aria-live="polite">
        <div class="panel-copy">
          <div class="panel-title">Temporary password generated</div>
          <div class="panel-user">User: {{ lastResetUserEmail }}</div>
          <div class="panel-password">{{ temporaryPassword }}</div>
          <div class="panel-note">
            This value is shown once. Share it securely with the user.
          </div>
        </div>
        <button
          mat-stroked-button
          color="primary"
          (click)="dismissTemporaryPassword()"
        >
          Dismiss
        </button>
      </section>
    }

    <section class="card">
      <div class="toolbar">
        <mat-form-field
          appearance="outline"
          class="search-field"
          subscriptSizing="dynamic"
        >
          <mat-label>Find user by email or username</mat-label>
          <input
            matInput
            [formControl]="searchControl"
            (keyup.enter)="onSearch()"
            placeholder="ex. user@company.com"
            aria-label="Search users"
          />
          <ng-icon name="HeroSearch" matSuffix class="icon-fw"></ng-icon>
        </mat-form-field>

        <div class="toolbar-actions">
          <button mat-flat-button color="primary" (click)="onSearch()">
            Search
          </button>
          <button mat-stroked-button (click)="clearSearch()">Clear</button>
        </div>
      </div>

      @if (loading) {
        <div class="loading">
          <mat-spinner diameter="36"></mat-spinner>
          <div>Loading users...</div>
        </div>
      }

      @if (!loading && loadError) {
        <div class="error-message">
          <div>Unable to load users. Please try again.</div>
          <button mat-stroked-button color="primary" (click)="loadUsers()">
            Retry
          </button>
        </div>
      }

      @if (!loading && !loadError && users.length === 0) {
        <div class="empty">
          <ng-icon name="HeroInbox" class="icon-fw"></ng-icon>
          <div>No users found for your search.</div>
        </div>
      }

      @if (!loading && !loadError && users.length > 0) {
        <div class="table-container">
          <table
            mat-table
            [dataSource]="dataSource"
            matSort
            class="users-table"
            aria-label="Users management table"
          >
            <ng-container matColumnDef="email">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>EMAIL</th>
              <td mat-cell *matCellDef="let user">{{ user.email || "-" }}</td>
            </ng-container>

            <ng-container matColumnDef="username">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>
                USERNAME
              </th>
              <td mat-cell *matCellDef="let user">
                {{ user.username || "-" }}
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="actions-header">
                ACTIONS
              </th>
              <td mat-cell *matCellDef="let user" class="actions-cell">
                <button
                  mat-icon-button
                  class="actions-trigger"
                  [matMenuTriggerFor]="actionsMenu"
                  [attr.aria-label]="'Open actions for ' + user.email"
                >
                  <ng-icon name="HeroDotsHorizontal" class="icon-fw"></ng-icon>
                </button>

                <mat-menu #actionsMenu="matMenu">
                  <button
                    mat-menu-item
                    class="actions-menu-item"
                    (click)="confirmResetPassword(user)"
                  >
                    Reset Password
                  </button>
                  <button
                    mat-menu-item
                    class="actions-menu-item"
                    (click)="confirmDeleteUser(user)"
                  >
                    Delete User
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr
              mat-row
              *matRowDef="
                let user;
                columns: displayedColumns;
                trackBy: trackByUser
              "
            ></tr>
          </table>

          <mat-paginator
            [pageSize]="pageSize"
            [pageSizeOptions]="[10, 20, 50]"
            showFirstLastButtons
            aria-label="Users table pagination"
          ></mat-paginator>
        </div>
      }
    </section>
  }
</div>
